import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { useDispatch } from 'react-redux';
import {
  Drawer,
  IconButton,
  TextField,
  CircularProgress,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Close, Info } from '@material-ui/icons';
import { toast } from '../../redux/actions/UI';
import { updateDraft } from '../../redux/services/drafts';
import {
  Container,
  Header,
  Body,
  Grey,
  Bold,
  Section,
  Small,
  Wrapper,
  Button,
  SectionRow,
} from '../Analytics/Components/SmartResendDrawer/SmartResendDrawer.styled';

const useStyles = makeStyles(theme => ({
  textField: {
    width: 270,
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },
}));

const SmartResendDrawer = ({
  setDrawerVisibility,
  isDrawerVisible,
  message,
  scheduleTime,
  scheduledResendMessage,
  setScheduledResendMessage,
}) => {
  const dispatch = useDispatch();
  const [isResendingInProgress, setResendingProgress] = useState(false);

  const originalSendTime = scheduleTime ? scheduleTime : Date.now() / 1000;
  const classes = useStyles();
  const closeDrawer = () => {
    setDrawerVisibility(false);
  };

  useEffect(() => {
    if (message) {
      const threeDaysFromSendTime = moment(
        (originalSendTime + 3 * 24 * 60 * 60) * 1000
      ).format('YYYY-MM-DDTHH:mm');
      setScheduledResendMessage({
        ...scheduledResendMessage,
        messageSubject: message.subject,
        resendDate: threeDaysFromSendTime,
      });
    }
  }, [message]);

  const minDate = moment((originalSendTime + 24 * 60 * 60) * 1000).format(
    'YYYY-MM-DDTHH:mm'
  );
  const maxDate = moment((originalSendTime + 10 * 24 * 60 * 60) * 1000).format(
    'YYYY-MM-DDTHH:mm'
  );

  const formattedDate = date => moment(date).format('MMMM D, YYYY [at] h:mma');

  const handleDateChange = event => {
    setScheduledResendMessage({
      ...scheduledResendMessage,
      resendDate: event.target.value,
    });
  };

  const handleMessageSubjectChange = event => {
    setScheduledResendMessage({
      ...scheduledResendMessage,
      messageSubject: event.target.value,
    });
  };

  const resendToNonOpeners = async () => {
    if (!scheduledResendMessage.messageSubject) {
      dispatch(
        toast('error', 'A subject line is required to resend a message.')
      );
      return;
    }
    try {
      setResendingProgress(true);
      await updateDraft({
        _id: message._id,
        smartResend: {
          resendDate: moment(scheduledResendMessage.resendDate).unix(),
          messageSubject: scheduledResendMessage.messageSubject,
        },
      });
      setScheduledResendMessage({
        ...scheduledResendMessage,
        confirmResend: true,
      });
      setResendingProgress(false);
      setDrawerVisibility(false);

      dispatch(toast('success', 'Re-Send to Non-openers has been saved.'));
    } catch (error) {
      console.error(error);
      dispatch(toast('error', 'Error saving resend to Non-openers.'));
    }
  };

  const cancelResendToNonOpeners = async () => {
    try {
      await updateDraft({
        _id: message._id,
        smartResend: null,
      });
      setScheduledResendMessage({
        ...scheduledResendMessage,
        confirmResend: false,
      });
      setDrawerVisibility(false);
      dispatch(toast('success', 'Resend to non-openers cancelled.'));
    } catch (error) {
      console.error(error);
      dispatch(toast('error', 'Error cancelling resend to Non-openers.'));
    }
  };

  const isResendBtnDisabled = !(
    moment(scheduledResendMessage.resendDate)?.isSameOrAfter(minDate) &&
    moment(scheduledResendMessage.resendDate)?.isSameOrBefore(maxDate) &&
    scheduledResendMessage.messageSubject
  );

  return (
    <Drawer open={isDrawerVisible} onClose={closeDrawer} anchor="right">
      <Container>
        <Header>
          <IconButton size={'small'} style={{ color: 'white' }}>
            <Close size={'small'} onClick={closeDrawer} />
          </IconButton>
          Resend Message to Non-Openers
        </Header>
        <Body>
          <Section>
            <Bold>Schedule Resend to Non-Openers</Bold>
            <Small>
              Messages can be resent to non-openers 1 - 10 days after original
              send date.
            </Small>
            <TextField
              id="datetime-local"
              type="datetime-local"
              variant="outlined"
              size="small"
              value={scheduledResendMessage.resendDate}
              onChange={handleDateChange}
              className={classes.textField}
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{
                min: minDate,
                max: maxDate,
              }}
            />
            <Wrapper>
              <Info style={{ color: '#F28501' }} />
              <Small>
                Tip: Try to schedule the resend <Bold>2 - 3 days</Bold> after
                the original message was sent. Scheduling a resend too soon, or
                too late, may result in a higher spam report rate.
              </Small>
            </Wrapper>
          </Section>
          <Section>
            <Bold>Subject line for resent message:</Bold>
            <TextField
              size="small"
              variant="outlined"
              defaultValue={scheduledResendMessage.messageSubject}
              style={{ width: '80%', paddingBottom: '10px' }}
              id="subject-line"
              onChange={handleMessageSubjectChange}
              error={!scheduledResendMessage.messageSubject}
              helperText={
                !scheduledResendMessage.messageSubject &&
                'Subject line is required.'
              }
            />
            <Wrapper>
              <Info style={{ color: '#F28501' }} />
              <Small>
                Tip: Try to keep your new subject line <Bold>the same</Bold> as
                the original message&apos;s subject line. The more you change
                the subject line for a resend, the more it will be reported as
                spam.
              </Small>
            </Wrapper>
          </Section>
          {message.preheader && (
            <Section>
              <Wrapper>
                <Grey>Preheader:</Grey> <Bold>{message.preheader}</Bold>
              </Wrapper>
            </Section>
          )}
          <Section>
            <SectionRow>
              <Button
                onClick={resendToNonOpeners}
                disabled={isResendBtnDisabled}
              >
                {isResendingInProgress ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  'Confirm Resend'
                )}
              </Button>{' '}
              <Button
                onClick={cancelResendToNonOpeners}
                disabled={isResendBtnDisabled}
                cancel={true}
              >
                Cancel
              </Button>
            </SectionRow>

            <Small isError={isResendBtnDisabled}>
              {isResendBtnDisabled
                ? !scheduledResendMessage.messageSubject
                  ? 'A subject line is required to resend a message.'
                  : 'Messages can only be resent to non-openers between 1 - 10 days after the original message was sent.'
                : `Message will resend to non-openers on ${formattedDate(
                    scheduledResendMessage.resendDate
                  )}`}
            </Small>
          </Section>
        </Body>
      </Container>
    </Drawer>
  );
};

export default SmartResendDrawer;
